import React, { Fragment, useCallback, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import TextInput from "@components/inputs/TextInput/TextInput";
import SelectInput from "@components/inputs/SelectInput/SelectInput";
import MediaSelector from "@components/inputs/MediaSelector/MediaSelector";
import Card from "@components/cards/Card/Card";
import Button from "@components/buttons/Button/Button";
import DeleteConfirmationPopup from "@components/modals/DeleteConfirmationPopup/DeleteConfirmationPopup";
import { MAX_MEMORIAL_TIMELINE_DESCRIPTION_LENGTH, MAX_MEMORIAL_TIMELINE_IMAGE_SIZE, MAX_MEMORIAL_TIMELINE_TITLE, MAX_MEMORIAL_TIMELINE_VIDEO_SIZE, MAX_MEMORIAL_TIMELINE_ENTRIES } from "@constants/validation";
import { isValidMaxLength } from "@util/validators";
import { MediaFile } from "@models/Common";
import { TimelineEntryAdd, TimelineEntryChange } from "@models/Timeline";
import styles from "./TimelineForm.module.css";

export interface TimelineData {
    id: string;
    year: number;
    month?: number;
    day?: number;
    title?: string;
    description?: string;
    media?: MediaFile | null;
}

export interface TimelineDataWithError extends TimelineData {
    hasError: boolean;
    mediaError: string;
}

interface TimelineFormProps {
    state: TimelineDataWithError[];
    onChange: (timelineEntries: TimelineDataWithError[]) => void;
    onAdd: (newEntry: TimelineEntryAdd) => void;
    onUpdate: (updatedEntry: TimelineEntryChange) => void;
    onDelete: (id: string) => void;
}

const TimelineForm: React.FC<TimelineFormProps> = ({
    state, onChange, onAdd, onUpdate, onDelete,
}) => {
    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(0);

    const validateYear = useCallback((val: number) => {
        if (val >= 1000 && val <= 9999) {
            return "";
        }
        return "Invalid Year";
    }, []);

    const validateTitle = useCallback((val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_TIMELINE_TITLE);
        }
        return "";
    }, []);

    const validateDescription = useCallback((val: string) => {
        if (val) {
            return isValidMaxLength(val, MAX_MEMORIAL_TIMELINE_DESCRIPTION_LENGTH);
        }
        return "";
    }, []);

    const addEntry = (index: number) => {
        if (state.length >= MAX_MEMORIAL_TIMELINE_ENTRIES) return;

        const newEntryId = uuidv4(); // Generate a unique ID for the new entry
        const newEntry: TimelineDataWithError = {
            id: newEntryId,
            year: 0,
            month: undefined,
            day: undefined,
            title: "",
            description: "",
            media: null,
            hasError: true, // Default to true as year empty
            mediaError: "",
        };

        const updatedEntries = [...state];

        if (index !== undefined) {
            updatedEntries.splice(index, 0, newEntry);
        } else {
            updatedEntries.push(newEntry);
        }

        onChange(updatedEntries);

        // Call the optional onAdd callback with the new entry's id
        if (onAdd) {
            const newEntryData: TimelineEntryAdd = {
                id: newEntryId,
                year: newEntry.year,
                month: newEntry.month,
                day: newEntry.day,
                title: newEntry.title,
                description: newEntry.description,
                media: newEntry.media || undefined,
            };
            onAdd(newEntryData);
        }
    };

    const getUpdatedFields = (
        original: TimelineDataWithError,
        updated: Partial<TimelineDataWithError>,
    ): Partial<TimelineEntryChange> => {
        const changes: Partial<TimelineEntryChange> = {};

        // Compare each field and add to changes only if it has changed
        if (updated.year !== undefined && updated.year !== original.year) {
            changes.year = updated.year;
        }
        if (updated.month !== undefined && updated.month !== original.month) {
            changes.month = updated.month;
        }
        if (updated.day !== undefined && updated.day !== original.day) {
            changes.day = updated.day;
        }
        if (updated.title !== undefined && updated.title !== original.title) {
            changes.title = updated.title;
        }
        if (updated.description !== undefined && updated.description !== original.description) {
            changes.description = updated.description;
        }
        if (updated.media !== undefined && updated.media !== original.media) {
            changes.media = updated.media || undefined;
        }

        return changes;
    };

    const updateEntry = (index: number, updatedData: Partial<TimelineDataWithError>) => {
        const updatedEntries = [...state];
        const originalEntry = updatedEntries[index];
        const entry = { ...originalEntry, ...updatedData };

        const yearErr = validateYear(entry.year);
        const titleErr = validateTitle(entry.title || "");
        const descriptionErr = validateDescription(entry.description || "");

        entry.hasError = Boolean(yearErr || titleErr || descriptionErr);

        updatedEntries[index] = entry;
        onChange(updatedEntries);

        // Call the optional onUpdate callback with the full entry if there are any changes
        if (onUpdate && Object.keys(updatedData).length > 0) {
            // @ts-expect-error
            onUpdate(entry); // Pass the full entry with both original and updated fields
        }
    };

    const removeEntry = (index: number) => {
        const deletedEntry = state[index];

        const updatedEntries = [...state];
        updatedEntries.splice(index, 1);
        onChange(updatedEntries);

        // Call the optional onDelete callback with the entry's id
        if (onDelete) {
            onDelete(deletedEntry.id);
        }
    };

    const monthOptions = [
        { label: "January", value: "1" },
        { label: "February", value: "2" },
        { label: "March", value: "3" },
        { label: "April", value: "4" },
        { label: "May", value: "5" },
        { label: "June", value: "6" },
        { label: "July", value: "7" },
        { label: "August", value: "8" },
        { label: "September", value: "9" },
        { label: "October", value: "10" },
        { label: "November", value: "11" },
        { label: "December", value: "12" },
    ];

    const dayOptions = Array.from({ length: 31 }, (_, i) => ({
        label: `${i + 1}`,
        value: `${i + 1}`,
    }));

    return (
        <div>
            {state.length < 1 && (
                <div className={styles.buttonContainer}>
                    <Button onClick={() => addEntry(0)}>Add Entry</Button>
                </div>
            )}

            <div>
                {state.map((entry, index) => (
                    <Fragment key={entry.id}>
                        <Card className={styles.timelineFormCard}>
                            {/* <div className={styles.timelineHeader}>
                                <div className={styles.controls}> */}
                                    {/* {(index > 0) && (
                                        <IconButton onClick={() => moveEntryUp(index)}>
                                            ▲
                                        </IconButton>
                                    )}
                                    {(index < state.length - 1) && (
                                        <IconButton onClick={() => moveEntryDown(index)}>
                                            ▼
                                        </IconButton>
                                    )} */}
                                {/* </div>
                                <IconButton onClick={() => {
                                    setShowConfirmPopup(true);
                                    setDeleteIndex(index);
                                }}
                                >
                                    <Delete fontSize="medium" />
                                </IconButton>
                            </div> */}
                            <TextInput
                                label="Year *"
                                id={`year-input-${index}`}
                                value={entry.year ? entry.year.toString() : ""}
                                onChange={(val) => updateEntry(index, { year: parseInt(val) })}
                                placeholder="Enter year..."
                                errorMessage={validateYear(entry.year)}
                            />
                            <SelectInput
                                label="Month"
                                id={`month-input-${index}`}
                                value={entry.month?.toString() || ""}
                                onChange={(val) => updateEntry(index, { month: val ? parseInt(val) : undefined })}
                                options={monthOptions}
                                disabled={!entry.year}
                            />
                            <SelectInput
                                label="Day"
                                id={`day-input-${index}`}
                                value={entry.day?.toString() || ""}
                                onChange={(val) => updateEntry(index, { day: val ? parseInt(val) : undefined })}
                                options={dayOptions}
                                disabled={!entry.month}
                            />
                            <TextInput
                                label="Title"
                                id={`title-input-${index}`}
                                value={entry.title || ""}
                                onChange={(val) => updateEntry(index, { title: val })}
                                placeholder="Enter title..."
                                errorMessage={validateTitle(entry.title || "")}
                            />
                            <TextInput
                                label="Description"
                                id={`description-input-${index}`}
                                value={entry.description || ""}
                                onChange={(val) => updateEntry(index, { description: val })}
                                placeholder="Enter description..."
                                multiline
                                height="100px"
                                errorMessage={validateDescription(entry.description || "")}
                            />
                            <MediaSelector
                                label="Image or Video"
                                id={`media-${index}`}
                                media={entry.media || ""}
                                maxSizeImage={MAX_MEMORIAL_TIMELINE_IMAGE_SIZE}
                                maxSizeVideo={MAX_MEMORIAL_TIMELINE_VIDEO_SIZE}
                                errorMessage={entry.mediaError}
                                onMediaChange={(file, error) => {
                                    updateEntry(index, {
                                        media: file as MediaFile,
                                        mediaError: error,
                                    });
                                }}
                            />
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    setShowConfirmPopup(true);
                                    setDeleteIndex(index);
                                }}
                            >
                                Remove Entry
                            </Button>
                        </Card>

                        {((index === state.length - 1)
                            && state.length < MAX_MEMORIAL_TIMELINE_ENTRIES) && (
                            <div className={styles.buttonContainer}>
                                <Button onClick={() => addEntry(index + 1)}>Add Entry</Button>
                            </div>
                        )}
                    </Fragment>
                ))}
                <p className={styles.note}>
                    Max
                    {" "}
                    {MAX_MEMORIAL_TIMELINE_ENTRIES}
                    {" "}
                    Entries
                </p>
            </div>
            <DeleteConfirmationPopup
                visible={showConfirmPopup}
                onConfirm={() => {
                    removeEntry(deleteIndex);
                    setShowConfirmPopup(false);
                }}
                onCancel={() => setShowConfirmPopup(false)}
            />
        </div>
    );
};

export default TimelineForm;
